// جدولة تنبيهات الأذان.
//
// النسخة الأولى كانت تعرض زر تفعيل (غير مربوط أصلاً بالواجهة) يُظهر إشعاراً
// واحداً فورياً ويقول "سيتم تنبيهك عند دخول وقت الأذان" — بلا أي جدولة.
//
// ثم تبيّن على محاكي أندرويد أن الاعتماد على Notification API وحده يجعل
// الميزة معطّلة على المنصّة التي تدعمها أفضل من غيرها: WebView لا يوفّر
// window.Notification، فكان التطبيق يقول "متصفحك لا يدعم الإشعارات".
//
// الآن ثلاثة مسارات، كلها من خطة واحدة (notificationPlan):
//  • أندرويد → المُجدول الأصلي عبر Adhan.setPlan: خطة سنةٍ كاملة يحفظها
//    ويسلّح قادمها بنفسه، فيعمل الأذان والتطبيق مغلق ولو لم يُفتح سنة.
//  • iOS → LocalNotifications: ثلاثة وستون إشعاراً وتذكيرٌ بفتح التطبيق بعدها.
//  • في المتصفح → Notification API، تعمل ما دام التطبيق مفتوحاً.
// الواجهة تذكر الفرق صراحة بدل الإيحاء بما لا يحدث.

import { buildNotificationPlan } from './notificationPlan';

// setTimeout يفيض فوق ~24.8 يوم؛ المتصفح يجدول ليومين لكن نحرس على كل حال
const MAX_TIMEOUT = 2_147_483_647;
const NATIVE_ID_BASE = 9000;

/*
  كم يوماً تغطّي الخطة.

  أندرويد يحفظها ويسلّحها بنفسه، فسنةٌ كاملة بلا فتح. والحجم (~٣٦٥٠ حدثاً)
  لا يُثقل: المُجدول لا يسلّح منها إلا يومين.
  iOS يقبل أربعة وستين إشعاراً معلّقاً لا أكثر، وثمانية أيام تملؤها ولو بدأنا بعد عشاء اليوم.
  والمتصفح لا يجدول إلا ما دامت الصفحة حيّة.
*/
const ANDROID_PLAN_DAYS = 365;
const IOS_PLAN_DAYS = 8;
const WEB_PLAN_DAYS = 2;
const IOS_MAX = 64;

let timers = [];

/* ----------------------------- كشف المنصّة ----------------------------- */

export const isNativeShell = () =>
  typeof window !== 'undefined' && Boolean(window.Capacitor?.isNativePlatform?.());

const nativePlugin = () => window.Capacitor?.Plugins?.LocalNotifications ?? null;

/** مُلحق الأذان — يرفع النداء صوتاً والتطبيق مغلق. */
const adhanPlugin = () => window.Capacitor?.Plugins?.Adhan ?? null;

const hasWebNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window;

/** هل يمكن للتنبيهات أن تعمل على هذه المنصّة أصلاً؟ */
export const isNotificationSupported = () =>
  isNativeShell() ? Boolean(nativePlugin()) : hasWebNotifications();

export function notificationPermission() {
  if (isNativeShell()) return 'prompt';
  return hasWebNotifications() ? Notification.permission : 'unsupported';
}

export async function requestNotificationPermission() {
  if (isNativeShell()) {
    const plugin = nativePlugin();
    if (!plugin) return 'unsupported';
    try {
      const res = await plugin.requestPermissions();
      return res?.display === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }

  if (!hasWebNotifications()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

/* ------------------------------- الجدولة ------------------------------- */

export function clearScheduledNotifications() {
  timers.forEach(clearTimeout);
  timers = [];

  const plugin = nativePlugin();
  if (plugin) {
    plugin
      .cancel({
        notifications: Array.from({ length: IOS_MAX }, (_, i) => ({ id: NATIVE_ID_BASE + i })),
      })
      .catch(() => {});
  }
}

function notifyWeb(title, body) {
  try {
    const n = new Notification(title, {
      body,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      tag: 'moslam-adhan',
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // بعض المتصفحات تمنع الإنشاء المباشر خارج Service Worker
  }
}

/**
 * يجدول الأذان وتذكير الأذكار القادمة.
 * @returns {number} عدد الأحداث المسلَّمة للنظام
 */
export function schedulePrayerNotifications(options) {
  clearScheduledNotifications();

  if (isNativeShell()) {
    const adhan = adhanPlugin();
    if (adhan) return scheduleAndroid(adhan, options);
    return scheduleIos(options);
  }

  if (notificationPermission() !== 'granted') return 0;

  const adhanOnly = buildNotificationPlan({ ...options, days: WEB_PLAN_DAYS, athkar: false });
  adhanOnly.forEach(({ at, title, body }) => {
    const delay = at - Date.now();
    if (delay <= 0 || delay > MAX_TIMEOUT) return;
    timers.push(setTimeout(() => notifyWeb(title, body), delay));
  });

  return timers.length;
}

/*
  أندرويد: الخطة كلها للمُجدول الأصلي.

  هو يحفظها، ويسلّح يومين منها، ويعيد التسليح كل اثنتي عشرة ساعة وبعد
  الإقلاع — فلا يتوقف الأذان إن لم يُفتح التطبيق. ويرسل الإشعار النصي
  بنفسه؛ لا LocalNotifications هنا، وإلا وصل الإشعار مرتين.
*/
function scheduleAndroid(adhan, { adhanSound = '', ...options }) {
  const events = buildNotificationPlan({ ...options, days: ANDROID_PLAN_DAYS }).map(
    ({ at, kind, title, body, prayer, athkar }) => ({ at, kind, title, body, prayer, athkar })
  );

  // وكيل Capacitor قد يُرجع thenable بلا catch، فيُغلَّف كي لا يرمي هنا
  Promise.resolve(adhan.setPlan({ events, sound: adhanSound })).catch((err) =>
    console.warn('تعذّرت جدولة الأذان:', err)
  );

  return events.length;
}

/*
  iOS: لا شيفرة في الخلفية، وحدٌّ من أربعة وستين إشعاراً.

  نملأ ثلاثة وستين، والأخير يطلب فتح التطبيق بعد آخرها بساعة: خيرٌ من
  أن ينقطع الأذان بصمت.

  الأذان أولاً، ثم الأذكار في ما بقي — ولا تتجاوز آخر أذانٍ مجدول. لو
  قُصّت الخطة المدموجة لغطّت الأذكارُ نصف المقاعد فانقطع الأذان بعد ثلاثة أيام.
*/
function scheduleIos(options) {
  const plugin = nativePlugin();
  if (!plugin) return 0;

  const plan = buildNotificationPlan({ ...options, days: IOS_PLAN_DAYS });
  const adhans = plan.filter((e) => e.kind === 'adhan').slice(0, IOS_MAX - 1);
  const lastAdhanAt = adhans.length ? adhans[adhans.length - 1].at : -Infinity;
  const athkar = plan
    .filter((e) => e.kind === 'athkar' && e.at <= lastAdhanAt)
    .slice(0, IOS_MAX - 1 - adhans.length);
  const events = [...adhans, ...athkar].sort((a, b) => a.at - b.at);
  if (events.length === 0) return 0;

  const notifications = events.map(({ at, kind, title, body, athkar }, i) => ({
    id: NATIVE_ID_BASE + i,
    title,
    body,
    schedule: { at: new Date(at), allowWhileIdle: true },
    // الإشعار نفسه ينادي، والنظام لا يقبل صوتاً فوق ثلاثين ثانية — فهو مطلع الأذان
    ...(kind === 'adhan' ? { sound: 'adhan.caf' } : { extra: { athkar } }),
  }));

  notifications.push({
    id: NATIVE_ID_BASE + notifications.length,
    title: 'افتح التطبيق لتجديد مواعيد الأذان',
    body: 'انتهت المواعيد المجدولة على جهازك',
    schedule: { at: new Date(events[events.length - 1].at + 3_600_000), allowWhileIdle: true },
  });

  plugin
    .schedule({ notifications })
    .catch((err) => console.warn('تعذرت جدولة التنبيهات:', err));

  return notifications.length;
}

/** وصف صادق لما تفعله التنبيهات على هذه المنصّة. */
export function notificationCapabilityNote() {
  if (isNativeShell()) return 'ستصلك التنبيهات حتى والتطبيق مغلق.';
  if (!hasWebNotifications()) return 'متصفحك لا يدعم إشعارات النظام.';
  return 'تعمل ما دام التطبيق مفتوحاً في المتصفح (ولو في الخلفية).';
}

/* --------------------------- حالة الإذن وإصلاحه --------------------------- */

/**
 * حالة الإذن الحقيقية دون إظهار نافذة الطلب.
 *
 * notificationPermission() تُرجع 'prompt' على المنصّة الأصلية لأنها
 * متزامنة، وملحق أندرويد لا يجيب إلا بوعد. وهذه تسأل الملحق فعلاً،
 * فنعرف عند الإقلاع أمُنح الإذن أم رُفض دون أن نزعج المستخدم بنافذة.
 */
export async function currentNotificationPermission() {
  if (isNativeShell()) {
    const plugin = nativePlugin();
    if (!plugin) return 'unsupported';
    try {
      const res = await plugin.checkPermissions();
      if (res?.display === 'granted') return 'granted';
      if (res?.display === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'prompt';
    }
  }
  return hasWebNotifications() ? Notification.permission : 'unsupported';
}

/**
 * يفتح إعدادات إشعارات التطبيق في النظام.
 *
 * لا مفتاح للتنبيهات في الواجهة: الأذان يعمل دائماً. فإن رفض المستخدم
 * الإذن على مستوى النظام لم يبق له طريق داخل التطبيق إلا هذا الزر —
 * وأندرويد لا يعيد عرض نافذة الطلب بعد رفضين.
 *
 * @returns {Promise<boolean>} هل فُتحت الإعدادات فعلاً؟
 */
export async function openNotificationSettings() {
  const plugin = window.Capacitor?.Plugins?.AppSettings;
  if (!plugin) return false;
  try {
    await plugin.open();
    return true;
  } catch {
    return false;
  }
}


/* ------------------------------ الأذان صوتاً ------------------------------ */

/** يوقف أذاناً يُرفع الآن. */
export async function stopAdhan() {
  const plugin = adhanPlugin();
  if (!plugin) return;
  try {
    await plugin.stop();
  } catch {
    // لا شيء يُرفع الآن
  }
}

/**
 * هل يُسمح بالمنبّه الدقيق؟
 *
 * أندرويد ١٢ فأحدث يشترط إذناً مستقلاً، وبدونه يؤجّل النظام المنبّه
 * لتوفير البطارية — فيتأخّر الأذان دقائق عن وقته.
 */
export async function canScheduleExactAdhan() {
  const plugin = adhanPlugin();
  if (!plugin) return true;
  try {
    const res = await plugin.canScheduleExact();
    return res?.value !== false;
  } catch {
    return true;
  }
}

/** يفتح إعداد «المنبّهات والتذكيرات» في النظام. */
export async function openExactAlarmSettings() {
  const plugin = adhanPlugin();
  if (!plugin) return false;
  try {
    await plugin.openExactAlarmSettings();
    return true;
  } catch {
    return false;
  }
}


/**
 * ينزّل صوت الأذان ويحفظه على الجهاز، ويعيد مساره المحلي.
 *
 * التنزيل مرة واحدة: الأذان يجب أن يُرفع بلا إنترنت، فلا يُعتمد على
 * الشبكة في وقته.
 */
export async function cacheAdhanSound(url) {
  const plugin = adhanPlugin();
  if (!plugin || !url) return '';
  try {
    const res = await plugin.cacheSound({ url });
    return res?.path ?? '';
  } catch (err) {
    console.warn('تعذّر تنزيل الأذان:', err);
    return '';
  }
}
